// import { UserStatus, UserType } from 'module/api/user/types';
import { useTranslation } from 'module/hooks/use-translation';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { PageLoading } from 'module/ui/components/atoms/loading';
import { ActionBar } from 'module/ui/components/molecules/action-bar';
import { HeaderWithIconTitle } from 'module/ui/components/molecules/page/page';
import { Argon as ArgonTemplate } from 'module/ui/components/templates/argon';
import { Content } from 'module/ui/components/templates/argon/components';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import { BlacklistedDomain, useGetBlacklistedDomains } from 'queries/emails';
import { useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { DomainsList } from './domains-list';
import { ManageDomainsDrawer } from './manage-domain-drawer/index';

const Argon = styled(ArgonTemplate)`
  ${Content} {
    border: none;
  }
  ${ActionBar} {
    box-shadow: none;
  }
`;

const ScrollableListContainer = styled.div`
  overflow-y: auto;
  min-height: 200px;
  max-height: calc(100vh - 220px);
`;

interface FieldDrawerState {
  visible: boolean;
  domain?: BlacklistedDomain;
}
interface ReturnProps {
  drawerState: FieldDrawerState;
  openDrawerInCreateMode: VoidFunction;
  openDrawerInEditMode: (domain: BlacklistedDomain) => void;
  closeDrawer: VoidFunction;
}
export const useManageDomainsDrawerState = (): ReturnProps => {
  const [drawerState, setDrawerState] = useState<FieldDrawerState>({
    visible: false,
  });
  const closeDrawer = useCallback(() => {
    setDrawerState({ visible: false });
  }, []);
  const openDrawerInCreateMode = useCallback(() => {
    setDrawerState({ visible: true });
  }, []);
  const openDrawerInEditMode = useCallback(
    (domain?: BlacklistedDomain) => {
      setDrawerState({ visible: true, domain });
    },
    [setDrawerState],
  );

  return {
    drawerState,
    openDrawerInCreateMode,
    openDrawerInEditMode,
    closeDrawer,
  };
};

export const BlacklistedDomainsPage = () => {
  const title = useTranslation('email_blacklist');
  const { data: emailList, isLoading: isEmailListLoading } =
    useGetBlacklistedDomains();
  const {
    drawerState,
    closeDrawer,
    openDrawerInCreateMode,
    openDrawerInEditMode,
  } = useManageDomainsDrawerState();

  return (
    <Argon
      documentTitle={title}
      title={
        <HeaderWithIconTitle
          icon={<FontAwesomeIcon icon={faEnvelope} size={20} />}
          title={title}
        />
      }
      description={
        <FormattedMessage id="settings.domain_blacklist.description" />
      }
      actions={
        isEmailListLoading
          ? {}
          : {
              secondary: (
                <Button
                  leftIcon={<FontAwesomeIcon icon={faPlus} />}
                  variant="primary"
                  onClick={openDrawerInCreateMode}
                >
                  <FormattedMessage id="add_domain" />
                </Button>
              ),
            }
      }
      content={
        isEmailListLoading ? (
          <PageLoading />
        ) : (
          <>
            <ScrollableListContainer>
              <DomainsList
                domains={emailList ?? []}
                onEdit={openDrawerInEditMode}
                onCreate={openDrawerInCreateMode}
              />
            </ScrollableListContainer>
            <ManageDomainsDrawer
              onClose={closeDrawer}
              visible={drawerState.visible}
              domain={drawerState.domain}
            />
          </>
        )
      }
    ></Argon>
  );
};
